import type {
  VirtualSlot as VirtualSlotType,
  SectionSlot,
  VirtualSlot,
  SectionWithSlots,
  Section,
  SectionSlot as SectionSlotType,
  SupportedLocale,
} from '@youshift/shared/types';
import {
  getCustomDateRange,
  divideSlotsIntoWeeks,
  localeNormalizer,
  SlotWithSpanning,
} from '@youshift/shared/utils';

import { Epa } from '../../layouts/IterationRootLayout/types';

type SectionToVirtualSlotDict = {
  [idSectionSlot: number]: number[];
};

/**
 * Returns a dictionary where the key is an id_virtual_slot and the value
 * is the total number of assignments for that virtual slot.
 */
export function getAssignmentsByVirtualSlot(
  epa: Epa,
  virtualSlotsForWeek: VirtualSlotType[],
): Record<number, number> {
  return Object.fromEntries(
    virtualSlotsForWeek.map(virtualSlot => {
      const totalAssignments = virtualSlot.section_slots.reduce(
        (total, idSectionSlot) => {
          const assignmentsForSlot =
            epa.assignmentsMap.bySectionSlot[idSectionSlot] || [];
          return total + assignmentsForSlot.length;
        },
        0,
      );

      return [virtualSlot.id_virtual_slot, totalAssignments];
    }),
  );
}

/**
 * Builds a dictionary mapping from a section slot to all the virtual slot IDs
 * that it belongs to.
 */
export function getSectionToVirtualSlotDict(
  virtualSlotsForWeek: VirtualSlotType[],
): SectionToVirtualSlotDict {
  const sectionToVirtualSlot: SectionToVirtualSlotDict = {};

  virtualSlotsForWeek.forEach(virtualSlot => {
    virtualSlot.section_slots.forEach(idSectionSlot => {
      if (!sectionToVirtualSlot[idSectionSlot]) {
        sectionToVirtualSlot[idSectionSlot] = [];
      }
      sectionToVirtualSlot[idSectionSlot].push(
        Number(virtualSlot.id_virtual_slot),
      );
    });
  });

  return sectionToVirtualSlot;
}

/**
 * Determines whether any virtual slot belonging to a given section slot
 * is already "full".
 */
export function getAreSlotsFull(
  assignmentsByVirtualSlot: Record<number, number> | undefined,
  sectionToVirtualSlotDict: SectionToVirtualSlotDict,
  virtualSlotsForWeek: VirtualSlotType[],
): Record<number, boolean> {
  const areSlotsFull: Record<number, boolean> = {};

  if (!assignmentsByVirtualSlot) return areSlotsFull;

  Object.entries(sectionToVirtualSlotDict).forEach(
    ([idSectionSlot, virtualSlotIds]) => {
      const anyFull = virtualSlotIds.some(idVirtualSlot => {
        const virtualSlot = virtualSlotsForWeek.find(
          vs => vs.id_virtual_slot === idVirtualSlot,
        );
        if (!virtualSlot) return false;
        const currentlyAssigned = assignmentsByVirtualSlot[idVirtualSlot] || 0;
        return currentlyAssigned === virtualSlot.max_need;
      });

      areSlotsFull[Number(idSectionSlot)] = anyFull;
    },
  );

  return areSlotsFull;
}

export const filterSlotsByMonth = <T extends SectionSlot | VirtualSlot>(
  slots: Record<number, T>,
  month: Date,
): Record<number, T> => {
  const startOfMonth = new Date(month.getUTCFullYear(), month.getUTCMonth(), 1);
  const startOfNextMonth = new Date(
    month.getUTCFullYear(),
    month.getUTCMonth() + 1,
    1,
  );

  return Object.fromEntries(
    Object.entries(slots).filter(([_, slot]) => {
      const slotStart = new Date(slot.start);
      return slotStart >= startOfMonth && slotStart < startOfNextMonth;
    }),
  ) as Record<number, T>;
};

/**
 * Gets the initial current month based on the date range.
 * Returns the current month if within range, otherwise returns the start month.
 */
export function getInitialCurrentMonth(start: string, end: string): Date {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const now = new Date();

  if (now >= startDate && now <= endDate) {
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), 1);
  }
  return new Date(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1);
}

/**
 * Filters sections with slots for the current month.
 */
export function filterSectionsByMonth<T extends SectionWithSlots>(
  sections: T[],
  currentMonth: Date,
): T[] {
  return sections.map(section => ({
    ...section,
    section_slots: filterSlotsByMonth(section.section_slots, currentMonth),
    virtual_slots: filterSlotsByMonth(section.virtual_slots, currentMonth),
  }));
}

/**
 * Gets the weeks array for a given month.
 */
export function getWeeksForMonth(currentMonth: Date, locale: string): Date[][] {
  const startOfMonth = new Date(
    currentMonth.getUTCFullYear(),
    currentMonth.getUTCMonth(),
    1,
  );
  const endOfMonth = new Date(
    currentMonth.getUTCFullYear(),
    currentMonth.getUTCMonth() + 1,
    0,
  );
  return getCustomDateRange(
    startOfMonth.toISOString(),
    endOfMonth.toISOString(),
    localeNormalizer(locale),
    'virgueria',
  );
}

/**
 * Divides slots into weeks for each section.
 */
export function getSlotsPerSectionPerWeek<T extends SectionWithSlots>(
  sections: T[],
  weeks: Date[][],
): Array<{
  section: Section;
  slotsPerWeek: SlotWithSpanning<SectionSlotType>[][];
  virtualSlotsPerWeek: SlotWithSpanning<VirtualSlotType>[][];
}> {
  return sections.map(({ section, section_slots, virtual_slots }) => ({
    section,
    slotsPerWeek: divideSlotsIntoWeeks<SectionSlotType>(weeks, section_slots),
    virtualSlotsPerWeek: divideSlotsIntoWeeks<VirtualSlotType>(
      weeks,
      virtual_slots,
    ),
  }));
}
